import { Component, Inject, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '@services/auth.service';
import { ArticoloRecente, DashboardService, StatisticheRedazione } from '@services/dashboard.service';
import { UsersService } from '@services/users.service';
import { StatoArticolo, statoArticolo } from '@shared/utils/stato-articolo.util';
import { dataOraPiena, tempoRelativo } from '@shared/utils/tempo-relativo.util';
import { PageComponent } from '../../layout/page/page.component';
import { COMMON_APP_CONFIG } from 'src/app/core/tokens/common-config.token';

/** Una casella con un numero e il filtro che lo apre nell'elenco. */
interface Riquadro {
  chiave: keyof Omit<StatisticheRedazione, 'ultimi'>;
  etichetta: string;
  icona: string;
  tono: 'neutro' | 'attenzione' | 'ok';
  /** Filtri con cui aprire l'elenco degli articoli. */
  filtri: Record<string, string>;
  spiegazione: string;
}

@Component({
  standalone: true,
  imports: [RouterLink, MatButtonModule, MatIconModule, MatProgressBarModule, MatTooltipModule, PageComponent],
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
})
export class DashboardComponent implements OnInit {
  readonly authService = inject(AuthService);
  private readonly dashboardService = inject(DashboardService);
  private readonly usersService = inject(UsersService);
  private readonly router = inject(Router);

  readonly statistiche = signal<StatisticheRedazione | null>(null);
  readonly caricamento = signal(false);
  readonly errore = signal<string | null>(null);

  readonly riquadri: Riquadro[] = [
    {
      chiave: 'daRevisionare',
      etichetta: 'Da revisionare',
      icona: 'mark_email_unread',
      tono: 'attenzione',
      filtri: { stato: 'bozza' },
      spiegazione: 'Arrivati dal modulo pubblico e ancora in bozza. Prima si scoprivano solo scorrendo l elenco.',
    },
    {
      chiave: 'bozze',
      etichetta: 'Bozze',
      icona: 'edit_note',
      tono: 'neutro',
      filtri: { stato: 'bozza' },
      spiegazione: 'Articoli non ancora pubblicati.',
    },
    {
      chiave: 'programmati',
      etichetta: 'Programmati',
      icona: 'schedule',
      tono: 'neutro',
      filtri: { stato: 'programmato' },
      spiegazione: 'Approvati con data futura: escono da soli.',
    },
    {
      chiave: 'pubblicatiOggi',
      etichetta: 'Pubblicati oggi',
      icona: 'task_alt',
      tono: 'ok',
      filtri: { stato: 'pubblicato' },
      spiegazione: 'Usciti dalla mezzanotte a ora.',
    },
  ];

  constructor(@Inject(COMMON_APP_CONFIG) private mainConfig: Required<{ api: { authUrl: string } }>) {}

  async ngOnInit(): Promise<void> {
    if (!this.authService.isAuthenticated()) return;

    const utente = await this.authService.attendiUtente();
    if (utente) {
      // Manutenzione, silenziosa: segna l'ultimo accesso.
      this.usersService.registraAccesso(utente).subscribe({ error: () => undefined });
    }

    this.caricaStatistiche();
  }

  caricaStatistiche(): void {
    this.caricamento.set(true);
    this.errore.set(null);
    this.dashboardService.statistiche().subscribe({
      next: (res) => {
        this.statistiche.set(res);
        this.caricamento.set(false);
      },
      error: (err) => {
        this.caricamento.set(false);
        this.errore.set(err?.error?.messaggio || err?.error?.message || 'Non e stato possibile leggere i numeri della redazione');
      },
    });
  }

  valore(chiave: Riquadro['chiave']): number | null {
    const dati = this.statistiche();
    return dati ? dati[chiave] : null;
  }

  apriElenco(riquadro: Riquadro): void {
    this.router.navigate(['/gestione-articoli/articoli'], { queryParams: riquadro.filtri });
  }

  saluto(): string {
    const nome = this.authService.user?.blogger;
    return nome ? `Ciao ${nome}` : 'Benvenuto';
  }

  stato(articolo: ArticoloRecente): StatoArticolo {
    return statoArticolo(articolo);
  }

  toccato(articolo: ArticoloRecente): string {
    if (!articolo.updatedAt) return '';
    return tempoRelativo(Math.floor(new Date(articolo.updatedAt).getTime() / 1000));
  }

  toccatoPreciso(articolo: ArticoloRecente): string {
    if (!articolo.updatedAt) return '';
    return dataOraPiena(Math.floor(new Date(articolo.updatedAt).getTime() / 1000));
  }

  loginUrl() {
    return this.mainConfig.api.authUrl;
  }
}
